import type { MeResponse } from "@lms/shared-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { fetchJson } from "../api/client";
import { isMock, type RuntimeConfig } from "../config";
import { cognitoAuth, mockAuth, type AuthAdapter, type FreshTokens, type Session, type SignInRoute } from "./auth";

/**
 * The auth template's AuthProvider: the adapter (Cognito or mock), the
 * session, and /v1/me — the server's view of who this is, which tenant,
 * which roles. The UI never derives any of that from the token.
 */
interface AuthState {
  status: "loading" | "signed-out" | "signed-in";
  config: RuntimeConfig;
  session?: Session | undefined;
  me?: MeResponse | undefined;
  /** `returnTo` is where the callback lands afterwards; defaults to the current location. */
  signIn: (returnTo?: string, route?: SignInRoute) => Promise<void>;
  signOut: () => Promise<void>;
  completeSignIn: () => Promise<string>;
  /** After a step-up, the fresh tokens replace the session's. */
  replaceTokens: (tokens: FreshTokens) => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ config, children }: { config: RuntimeConfig; children: ReactNode }) {
  const [adapter, setAdapter] = useState<AuthAdapter>();
  // The callback page (and an eager click on Sign in) can ask for the adapter before the first
  // effect has run; anything that needs it waits rather than quietly doing nothing.
  const [adapterReady] = useState(() => {
    let resolve!: (a: AuthAdapter) => void;
    const promise = new Promise<AuthAdapter>((r) => (resolve = r));
    return { promise, resolve };
  });
  const [session, setSession] = useState<Session | null>();
  const [me, setMe] = useState<MeResponse>();
  const [meFailed, setMeFailed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let a: AuthAdapter;
      try {
        a = isMock ? mockAuth() : cognitoAuth(config);
      } catch {
        // This host serves no tenant (no client id): the shell is public, sign-in is not offered.
        setSession(null);
        return;
      }
      const s = await a.getSession();
      if (cancelled) return;
      setAdapter(a);
      adapterReady.resolve(a);
      setSession(s);
      if (s) {
        try {
          setMe(await fetchJson<MeResponse>(config, s, "/v1/me"));
        } catch {
          setMeFailed(true);
        }
      }
    })().catch(() => setSession(null));
    return () => {
      cancelled = true;
    };
  }, [config, adapterReady]);

  const signIn = useCallback(async (returnTo?: string, route?: SignInRoute) => (await adapterReady.promise).signIn(returnTo ?? location.pathname + location.search, route), [location, adapterReady]);
  const signOut = useCallback(async () => adapter?.signOut(), [adapter]);
  const completeSignIn = useCallback(async () => (await adapterReady.promise).completeSignIn(), [adapterReady]);
  const replaceTokens = useCallback(
    async (tokens: FreshTokens) => {
      const s = await (await adapterReady.promise).replaceTokens(tokens);
      if (s) setSession(s);
    },
    [adapterReady],
  );
  const refreshMe = useCallback(async () => {
    if (session) setMe(await fetchJson<MeResponse>(config, session, "/v1/me"));
  }, [config, session]);

  const value = useMemo<AuthState>(
    () => ({
      status: session === undefined ? "loading" : session === null || meFailed ? "signed-out" : me ? "signed-in" : "loading",
      config,
      session: session ?? undefined,
      me,
      signIn,
      signOut,
      completeSignIn,
      replaceTokens,
      refreshMe,
    }),
    [config, session, me, meFailed, signIn, signOut, completeSignIn, replaceTokens, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
